// Ported from the onboarding prototype's DashboardHandoff (approved timing,
// 14 Oct 2026). Short-lived, inert visual copies bridge the introduction and
// the real vault dashboard, which is already rendered underneath the overlay.
// Nothing here creates or changes a memory.

const ease = 'cubic-bezier(.22,1,.36,1)';
const journeyEase = 'cubic-bezier(.65,0,.2,1)';
// Milliseconds from "Continue to Memories".
const timing = {
  fade: 1400,
  fadeDelay: 180,
  glow: 1200,
  liftDelay: 0,
  lift: 2000,
  travel: 2200,
  flight: 1400,
  tooltip: 3800,
  tooltipFade: 650,
  reveal: 5250,
  end: 7570,
};

type Rect = { left: number; top: number; width: number; height: number };
type Captured = {
  copy: HTMLElement;
  width: number;
  height: number;
  rect: Rect;
};
type MediaCapture = Captured & {
  id: string;
  kind: string;
  play: Captured | null;
  duration: Captured | null;
};

export type HandoffSnapshot =
  | { reduced: true }
  | {
      reduced: false;
      backdrop: HTMLElement;
      button: Captured | null;
      media: MediaCapture[];
    };

const rectOf = (node: Element): Rect => {
  const r = node.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

const reducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function visualCopy(node: HTMLElement) {
  const copy = node.cloneNode(true) as HTMLElement;
  const originals = [node, ...node.querySelectorAll<HTMLElement>('*')];
  const copies = [copy, ...copy.querySelectorAll<HTMLElement>('*')];
  originals.forEach((original, index) => {
    const style = getComputedStyle(original);
    const target = copies[index];
    for (let i = 0; i < style.length; i += 1) {
      const property = style[i];
      target.style.setProperty(property, style.getPropertyValue(property));
    }
    target.removeAttribute('id');
    target.removeAttribute('autofocus');
    Object.assign(target.style, {
      animation: 'none',
      transition: 'none',
      pointerEvents: 'none',
    });
  });
  copy
    .querySelectorAll('.button-invitation-glow')
    .forEach((element) => element.remove());
  const width = node.offsetWidth || node.getBoundingClientRect().width;
  const height = node.offsetHeight || node.getBoundingClientRect().height;
  Object.assign(copy.style, {
    position: 'absolute',
    inset: 'auto',
    left: '0',
    top: '0',
    margin: '0',
    width: `${width}px`,
    height: `${height}px`,
    minWidth: '0',
    maxWidth: 'none',
    boxSizing: 'border-box',
    transform: 'none',
    transformOrigin: '0 0',
    visibility: 'visible',
    opacity: '1',
  });
  copy.setAttribute('aria-hidden', 'true');
  copy.inert = true;
  return { copy, width, height };
}

function capture(node: HTMLElement | null): Captured | null {
  if (!node) return null;
  const rect = rectOf(node);
  return rect.width > 0 && rect.height > 0
    ? { ...visualCopy(node), rect }
    : null;
}

function freezeBackdrop(node: HTMLElement) {
  const clone = node.cloneNode(true) as HTMLElement;
  const box = node.getBoundingClientRect();
  Object.assign(clone.style, {
    position: 'absolute',
    inset: '0 auto auto 0',
    margin: '0',
    left: `${box.left}px`,
    top: `${box.top}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    pointerEvents: 'none',
  });
  clone
    .querySelectorAll('[id]')
    .forEach((element) => element.removeAttribute('id'));
  clone
    .querySelectorAll<HTMLElement>(
      '.intro-next,[data-carrier][data-memory-id] .carrier-image',
    )
    .forEach((element) => {
      // eslint-disable-next-line no-param-reassign
      element.style.visibility = 'hidden';
    });
  clone.setAttribute('aria-hidden', 'true');
  clone.inert = true;
  return clone;
}

// Read the outgoing introduction before it unmounts.
export function captureIntroduction(outgoing: HTMLElement): HandoffSnapshot {
  if (reducedMotion() || typeof outgoing.animate !== 'function')
    return { reduced: true };
  const media = [
    ...outgoing.querySelectorAll<HTMLElement>('[data-carrier][data-memory-id]'),
  ]
    .map((node): MediaCapture | null => {
      const source = capture(node.querySelector<HTMLElement>('.carrier-image'));
      if (!source || Number(getComputedStyle(node).opacity) < 0.05) return null;
      return {
        id: node.dataset.memoryId ?? '',
        kind: node.dataset.mediaKind ?? 'photo',
        ...source,
        play: capture(node.querySelector<HTMLElement>('.scene-video-play')),
        duration: capture(
          node.querySelector<HTMLElement>('.scene-video-duration'),
        ),
      };
    })
    .filter((item): item is MediaCapture => !!item);
  return {
    reduced: false,
    backdrop: freezeBackdrop(outgoing),
    button: capture(outgoing.querySelector<HTMLElement>('.intro-next')),
    media,
  };
}

const inViewport = (rect: Rect) =>
  rect.width > 0 &&
  rect.height > 0 &&
  rect.left + rect.width > 0 &&
  rect.top + rect.height > 0 &&
  rect.left < window.innerWidth &&
  rect.top < window.innerHeight;

// Inside the real page, never the overlay's own copies.
const outside = (overlay: HTMLElement, selector: string) =>
  [...document.querySelectorAll<HTMLElement>(selector)].filter(
    (node) => !overlay.contains(node),
  );

type HandoffOptions = {
  overlay: HTMLElement;
  // Painted above the dashboard until the reveal.
  cover: HTMLElement;
  flights: HTMLElement;
  status: HTMLElement | null;
  snapshot: HandoffSnapshot;
  // Album of each picture, for a card when its own isn't on the dashboard.
  albumOf: (memoryId: string) => number | undefined;
  onDone: () => void;
};

export class DashboardHandoff {
  private animations: Animation[] = [];

  private hidden: { node: HTMLElement; visibility: string }[] = [];

  private cleanups: (() => void)[] = [];

  private running = false;

  private options: HandoffOptions;

  constructor(options: HandoffOptions) {
    this.options = options;
  }

  start() {
    const { overlay, cover, flights, status, snapshot } = this.options;
    this.running = true;
    window.addEventListener('resize', this.interrupt);
    window.addEventListener('keydown', this.onKeyDown, true);
    overlay.addEventListener('pointerdown', this.interrupt);
    overlay.addEventListener('wheel', this.interrupt, { passive: true });
    overlay.addEventListener('touchmove', this.interrupt, { passive: true });
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    preference.addEventListener('change', this.interrupt);
    this.cleanups.push(() => {
      window.removeEventListener('resize', this.interrupt);
      window.removeEventListener('keydown', this.onKeyDown, true);
      overlay.removeEventListener('pointerdown', this.interrupt);
      overlay.removeEventListener('wheel', this.interrupt);
      overlay.removeEventListener('touchmove', this.interrupt);
      preference.removeEventListener('change', this.interrupt);
    });
    if (snapshot.reduced || typeof cover.animate !== 'function') {
      this.finish(true);
      return;
    }
    overlay.focus({ preventScroll: true });
    if (status)
      status.textContent = 'Opening your Vault. Press Escape to continue.';
    flights.append(snapshot.backdrop);
    this.animate(snapshot.backdrop, [{ opacity: 1 }, { opacity: 0 }], {
      delay: timing.fadeDelay,
      duration: timing.fade,
      easing: 'cubic-bezier(.4,0,.2,1)',
    });
    this.buttonFlight(
      snapshot.button,
      outside(overlay, '[data-onboarding-new]')[0] ?? null,
    );
    this.tooltipFlight(
      outside(overlay, '[data-onboarding-tooltip]')[0] ?? null,
    );
    snapshot.media.forEach((media, index) => {
      this.flight(media, this.destinationFor(media.id), {
        delay: timing.travel + index * 70,
        duration: timing.flight,
      });
    });
    // The real dashboard is underneath, with identical state.
    const reveal = this.animate(cover, [{ opacity: 1 }, { opacity: 0 }], {
      delay: timing.reveal,
      duration: timing.end - timing.reveal,
      easing: 'cubic-bezier(.55,0,.45,1)',
    });
    reveal.finished.then(
      () => {
        if (this.running) this.finish(true);
      },
      () => {},
    );
  }

  interrupt = () => this.finish();

  onKeyDown = (event: KeyboardEvent) => {
    if (!this.running || !['Tab', 'Escape', 'Enter', ' '].includes(event.key))
      return;
    // The first keyboard action reveals the real controls before using them.
    if (event.key === 'Tab' && event.shiftKey) {
      this.finish();
      return;
    }
    event.preventDefault();
    this.finish(true);
  };

  // Clean up without reporting the end (the host is going away).
  cancel() {
    this.finish(false, false);
  }

  finish(focus = false, notify = true) {
    if (!this.running) return;
    this.running = false;
    this.cleanups.forEach((cleanup) => cleanup());
    this.cleanups = [];
    this.animations.forEach((animation) => animation.cancel());
    this.animations = [];
    this.hidden.forEach(({ node, visibility }) => {
      // eslint-disable-next-line no-param-reassign
      node.style.visibility = visibility;
    });
    this.hidden = [];
    this.options.flights.replaceChildren();
    if (this.options.status) this.options.status.textContent = '';
    if (focus)
      outside(this.options.overlay, '[data-onboarding-new]')[0]?.focus({
        preventScroll: true,
      });
    if (notify) this.options.onDone();
  }

  // The picture's own card when it's on screen, else its album's card.
  private destinationFor(memoryId: string) {
    const { overlay, albumOf } = this.options;
    const card = outside(
      overlay,
      '[data-memory-id] [data-handoff-memory]',
    ).find(
      (node) =>
        node.closest<HTMLElement>('[data-memory-id]')?.dataset.memoryId ===
          memoryId && inViewport(rectOf(node)),
    );
    if (card) return card;
    const albumId = albumOf(memoryId);
    if (albumId === undefined) return null;
    return (
      outside(
        overlay,
        `[data-album-id="${albumId}"] [data-handoff-album]`,
      ).find((node) => inViewport(rectOf(node))) ?? null
    );
  }

  private hide(node: HTMLElement) {
    this.hidden.push({ node, visibility: node.style.visibility });
    // eslint-disable-next-line no-param-reassign
    node.style.visibility = 'hidden';
  }

  private animate(
    node: HTMLElement,
    frames: Keyframe[],
    options: KeyframeAnimationOptions,
  ) {
    const animation = node.animate(frames, {
      easing: ease,
      fill: 'both',
      ...options,
    });
    this.animations.push(animation);
    animation.finished.catch(() => {});
    return animation;
  }

  private lift(
    node: HTMLElement,
    {
      travel = timing.travel,
      end = timing.travel + timing.flight,
    }: { travel?: number; end?: number } = {},
  ) {
    this.animate(
      node,
      [
        { transform: 'scale(1)', offset: 0 },
        {
          transform: 'scale(1)',
          offset: timing.liftDelay / end,
          easing: 'cubic-bezier(.4,0,.2,1)',
        },
        {
          transform: 'scale(1.05)',
          offset: (timing.liftDelay + timing.lift) / end,
        },
        { transform: 'scale(1.05)', offset: travel / end, easing: journeyEase },
        { transform: 'scale(1)', offset: 1 },
      ],
      { duration: end, easing: 'linear' },
    );
  }

  private follow(animation: Animation, draw: (progress: number) => void) {
    let frame = 0;
    const tick = () => {
      const progress = animation.effect?.getComputedTiming().progress ?? 0;
      draw(progress ?? 0);
      if (this.running) frame = requestAnimationFrame(tick);
    };
    tick();
    this.cleanups.push(() => cancelAnimationFrame(frame));
  }

  // The CTA becomes the real New button along a shallow curve; its capsule
  // changes width without stretching the labels.
  private buttonFlight(source: Captured | null, target: HTMLElement | null) {
    if (!source || !target || !source.rect.width || !source.rect.height) return;
    const end = rectOf(target);
    if (!end.width || !end.height) return;
    const start = source.rect;
    const destination = visualCopy(target);
    const style = getComputedStyle(target);
    const frame = document.createElement('div');
    frame.className = 'gs-handoff-flight gs-handoff-button';
    Object.assign(frame.style, {
      width: `${start.width}px`,
      height: `${start.height}px`,
      zIndex: '4',
    });
    const surface = document.createElement('div');
    surface.className = 'gs-handoff-lift';
    Object.assign(surface.style, {
      width: `${start.width}px`,
      height: `${start.height}px`,
    });
    frame.append(surface);
    this.lift(surface);
    // Three overlapping solids form one capsule. Only its middle stretches;
    // round ends and native-size labels never squash during the width change.
    const pill = document.createElement('div');
    pill.className = 'gs-handoff-pill';
    const left = document.createElement('i');
    const middle = document.createElement('i');
    const right = document.createElement('i');
    [left, middle, right].forEach((node) =>
      Object.assign(node.style, {
        position: 'absolute',
        left: '0',
        top: '0',
        height: `${start.height}px`,
        background: style.backgroundColor,
        transformOrigin: '0 0',
      }),
    );
    [left, right].forEach((cap) =>
      Object.assign(cap.style, {
        width: `${start.height}px`,
        borderRadius: '50%',
      }),
    );
    Object.assign(middle.style, {
      left: `${start.height / 2}px`,
      width: `${start.width - start.height}px`,
    });
    pill.append(left, middle, right);
    surface.append(pill, source.copy, destination.copy);
    [source.copy, destination.copy].forEach((copy) =>
      Object.assign(copy.style, {
        background: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
        outline: 'none',
      }),
    );
    const travel = {
      delay: timing.travel,
      duration: timing.flight,
      easing: journeyEase,
    };
    const shape = {
      delay: timing.travel + 500,
      duration: timing.flight - 500,
      easing: journeyEase,
    };
    const scaleY = end.height / start.height;
    const x = start.left;
    const y = start.top;
    const dx = end.left - start.left;
    const dy = end.top - start.top;
    const length = Math.hypot(dx, dy) || 1;
    const bend = 28;
    const cx = x + dx / 2 - (dy / length) * bend;
    const cy = y + dy / 2 + (dx / length) * bend;
    this.animate(
      frame,
      Array.from({ length: 41 }, (_, index) => {
        const p = index / 40;
        const q = 1 - p;
        return {
          offset: p,
          transform: `translate3d(${q * q * x + 2 * q * p * cx + p * p * (x + dx)}px,${q * q * y + 2 * q * p * cy + p * p * (y + dy)}px,0)`,
        };
      }),
      travel,
    );
    const geometry = this.animate(
      left,
      [{ transform: 'scale(1)' }, { transform: `scale(${scaleY})` }],
      shape,
    );
    this.animate(
      right,
      [
        { transform: `translateX(${start.width - start.height}px) scale(1)` },
        {
          transform: `translateX(${end.width - end.height}px) scale(${scaleY})`,
        },
      ],
      shape,
    );
    this.animate(
      middle,
      [
        { transform: 'translateX(0) scale(1,1)' },
        {
          transform: `translateX(${(end.height - start.height) / 2}px) scale(${(end.width - end.height) / (start.width - start.height)},${scaleY})`,
        },
      ],
      shape,
    );
    this.animate(
      source.copy,
      [
        { transform: 'translate(0,0)' },
        {
          transform: `translate(${(end.width - start.width) / 2}px,${(end.height - start.height) / 2}px)`,
        },
      ],
      shape,
    );
    this.animate(
      destination.copy,
      [
        {
          transform: `translate(${(start.width - end.width) / 2}px,${(start.height - end.height) / 2}px)`,
        },
        { transform: 'translate(0,0)' },
      ],
      shape,
    );
    this.animate(
      source.copy,
      [
        { opacity: 1, filter: 'blur(0px)' },
        { opacity: 0, filter: 'blur(3px)' },
      ],
      { delay: timing.travel + 600, duration: 450, easing: 'ease-in-out' },
    );
    this.animate(
      destination.copy,
      [
        { opacity: 0, filter: 'blur(3px)' },
        { opacity: 1, filter: 'blur(0px)' },
      ],
      { delay: timing.travel + 740, duration: 450, easing: 'ease-in-out' },
    );
    const halo = document.createElement('div');
    halo.className = 'button-halo';
    surface.prepend(halo);
    this.animate(
      halo,
      [
        { opacity: 0, offset: 0, easing: 'ease-in-out' },
        { opacity: 1, offset: timing.glow / timing.end },
        {
          opacity: 1,
          offset: (timing.end - 550) / timing.end,
          easing: 'ease-in-out',
        },
        { opacity: 0.62, offset: 1 },
      ],
      { duration: timing.end, easing: 'linear' },
    );
    this.follow(geometry, (p) => {
      halo.style.width = `${start.width + (end.width - start.width) * p}px`;
      halo.style.height = `${start.height + (end.height - start.height) * p}px`;
    });
    this.hide(target);
    this.options.flights.append(frame);
  }

  // A picture travels to its card, changing its crop around an undistorted
  // image; with nowhere to land it fades where it is.
  private flight(
    source: MediaCapture,
    target: HTMLElement | null,
    { delay, duration }: { delay: number; duration: number },
  ) {
    if (!source.rect.width || !source.rect.height) return;
    const start = source.rect;
    const end = target ? rectOf(target) : null;
    const frame = document.createElement('div');
    frame.className = 'gs-handoff-flight';
    Object.assign(frame.style, {
      width: `${start.width}px`,
      height: `${start.height}px`,
      zIndex: '2',
      transform: `translate3d(${start.left}px,${start.top}px,0)`,
    });
    const surface = document.createElement('div');
    surface.className = 'gs-handoff-recording gs-handoff-lift';
    surface.style.borderRadius = target
      ? getComputedStyle(target).borderRadius
      : '8px';
    frame.append(surface);
    this.lift(surface, { travel: delay, end: delay + duration });
    const image =
      source.copy.querySelector<HTMLImageElement>('img:not([hidden])');
    const placeholder = source.copy.querySelector('.scene-object-art');
    const poster =
      (image?.cloneNode() as HTMLElement | undefined) ||
      (placeholder?.cloneNode(true) as HTMLElement | undefined);
    if (!poster) {
      surface.append(source.copy);
      Object.assign(source.copy.style, { width: '100%', height: '100%' });
    } else {
      Object.assign(poster.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        maxWidth: 'none',
        padding: '0',
        objectFit: 'cover',
        objectPosition: '50% 50%',
        transform: 'none',
        visibility: 'visible',
        opacity: '1',
      });
      surface.append(poster);
    }
    const play =
      source.kind === 'video' ? document.createElement('span') : null;
    if (play) {
      play.className = 'gs-handoff-play';
      play.textContent = '▶';
      surface.append(play);
    }
    const playStart = source.play?.rect.width || 28;
    const playEnd = 40;
    if (source.duration) {
      const label = document.createElement('span');
      label.className = 'gs-handoff-duration';
      label.textContent = source.duration.copy.textContent;
      surface.append(label);
      this.animate(label, [{ opacity: 1 }, { opacity: 0 }], {
        delay: delay + duration * 0.75,
        duration: duration * 0.25,
        easing: 'linear',
      });
    }
    this.options.flights.append(frame);
    if (!end) {
      this.animate(frame, [{ opacity: 1 }, { opacity: 0 }], {
        delay: timing.fadeDelay,
        duration: timing.fade,
        easing: 'cubic-bezier(.4,0,.2,1)',
      });
      return;
    }
    if (target) this.hide(target);
    const movement = this.animate(
      frame,
      [
        { transform: `translate3d(${start.left}px,${start.top}px,0)` },
        { transform: `translate3d(${end.left}px,${end.top}px,0)` },
      ],
      { delay, duration, easing: journeyEase },
    );
    this.follow(movement, (p) => {
      frame.style.width = `${start.width + (end.width - start.width) * p}px`;
      frame.style.height = `${start.height + (end.height - start.height) * p}px`;
      if (play) {
        const size = `${playStart + (playEnd - playStart) * p}px`;
        play.style.width = size;
        play.style.height = size;
      }
    });
  }

  // The tooltip appears before the dashboard does (the real one is hidden
  // until the handoff ends).
  private tooltipFlight(tooltip: HTMLElement | null) {
    const source = capture(tooltip);
    if (!source) return;
    source.copy.querySelectorAll<HTMLElement>('*').forEach((node) => {
      // eslint-disable-next-line no-param-reassign
      node.style.visibility = 'visible';
    });
    const frame = document.createElement('div');
    frame.className = 'gs-handoff-tooltip';
    Object.assign(frame.style, {
      left: `${source.rect.left}px`,
      top: `${source.rect.top}px`,
    });
    frame.append(source.copy);
    this.options.flights.append(frame);
    this.animate(
      source.copy,
      [
        { opacity: 0, transform: 'translateY(4px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      {
        delay: timing.tooltip,
        duration: timing.tooltipFade,
        easing: 'ease-in-out',
      },
    );
  }
}
